import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EventResponse, EventExpenseResponse } from '../../../../../../../../../../@shared/models/response.module';
import { EventsService } from '../../../../../../../../../../@shared/services/events-service';
import { ToastService } from '../../../../../../../../../../@core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-add-expense',
  standalone: false,
  templateUrl: './add-expense.html',
  styleUrl: './add-expense.css'
})
export class AddExpense implements OnInit {
  @Input() event!: EventResponse;
  @Input() expense?: EventExpenseResponse;
  @Input() isEdit: boolean = false;

  title: string = '';
  description: string = '';
  amount: number = 0;
  currency: string = 'NOK';

  selectedFiles: File[] = [];
  uploading: boolean = false;
  saving: boolean = false;
  submitted: boolean = false;

  // Lightbox
  lightboxImage: string | null = null;

  currencies = [
    { value: 'NOK', label: 'NOK' },
    { value: 'SEK', label: 'SEK' },
    { value: 'EUR', label: 'EUR' },
    { value: 'USD', label: 'USD' },
    { value: 'GBP', label: 'GBP' },
    { value: 'DKK', label: 'DKK' }
  ];

  // Validation helpers
  get isTitleInvalid(): boolean {
    return this.submitted && !this.title.trim();
  }

  get isAmountInvalid(): boolean {
    return this.submitted && (!this.amount || this.amount <= 0);
  }

  get isFormValid(): boolean {
    return !!(this.title.trim() && this.amount && this.amount > 0);
  }

  constructor(
    public activeModal: NgbActiveModal,
    private eventsService: EventsService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    // Initialize currency from event's default_currency (which was set during event creation)
    const defaultCurrency = this.event?.default_currency || 'NOK';
    this.currency = defaultCurrency;

    if (this.expense && this.isEdit) {
      this.title = this.expense.title || '';
      this.description = this.expense.description || '';
      this.amount = this.expense.amount || 0;
      this.currency = defaultCurrency; // Always use event's default_currency
    }
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.selectedFiles = Array.from(input.files);
    }
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
  }

  async save() {
    this.submitted = true;

    if (!this.isFormValid) {
      this.toastService.error(this.translateService.instant('manageEvent.addExpense.validationError'));
      return;
    }

    this.saving = true;

    try {
      if (this.isEdit && this.expense) {
        // Update expense
        this.eventsService.updateEventExpense({
          expense_id: this.expense.expense_id,
          title: this.title.trim(),
          description: this.description,
          amount: this.amount,
          currency: this.currency
        }).subscribe({
          next: async (result) => {
            // Upload new images if any
            if (this.selectedFiles.length > 0) {
              await this.uploadImages(result.expense_id);
            }
            this.saving = false;
            this.toastService.success(this.translateService.instant('manageEvent.addExpense.updateSuccess'));
            this.activeModal.close(result);
          },
          error: (err) => {
            this.saving = false;
            this.toastService.error(err.error?.reason || this.translateService.instant('manageEvent.addExpense.updateError'));
          }
        });
      } else {
        // Create expense
        this.eventsService.createEventExpense({
          event_id: this.event.event_id,
          title: this.title.trim(),
          description: this.description,
          amount: this.amount,
          currency: this.currency
        }).subscribe({
          next: async (result) => {
            // Upload images if any
            if (this.selectedFiles.length > 0) {
              await this.uploadImages(result.expense_id);
            }
            this.saving = false;
            this.toastService.success(this.translateService.instant('manageEvent.addExpense.success'));
            this.activeModal.close(result);
          },
          error: (err) => {
            this.saving = false;
            this.toastService.error(err.error?.reason || this.translateService.instant('manageEvent.addExpense.error'));
          }
        });
      }
    } catch (error) {
      this.saving = false;
      this.toastService.error(this.translateService.instant('manageEvent.addExpense.unexpectedError'));
    }
  }

  async uploadImages(expenseId: number) {
    this.uploading = true;
    for (let i = 0; i < this.selectedFiles.length; i++) {
      const file = this.selectedFiles[i];
      try {
        await this.eventsService.uploadExpenseImage(expenseId, file, i).toPromise();
      } catch (error) {
        console.error('Error uploading image:', error);
      }
    }
    this.uploading = false;
  }

  openLightbox(imageUrl: string) {
    this.lightboxImage = imageUrl;
  }

  closeLightbox() {
    this.lightboxImage = null;
  }
}
